import StepRequestMethod from './components/StepRequestMethod';
import StepDocuments from './components/StepDocuments';
import StepSettings from './components/StepSettings';
import StepShare from './components/StepShare';
import StepGrantAccess from './components/StepGrantAccess';
import StepTemplate from './components/StepTemplate';
import {
  DATA_ROOM_RFI_CREATION_TYPE,
  DATA_ROOM_RFI_FILE_TYPES,
  DataRoomRFICreationType,
  PARSED_SPREADSHEET_RFI_ROW_STATUS,
  ParsedSpreadsheetRFIRow,
  RfiDocumentOption,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import {
  getArrayLength,
  getIsArrayEmpty,
  getIsDesc,
  getSortBy,
  getSortComparator,
  normalizeText,
} from 'utils';
import { NOTIFICATION_MEDIUM, NotificationMedium } from 'models/Notification';
import { RFI_LIST_STATUS_FILTER_ALL } from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';

export const DOCUMENT_OPTIONS = Object.values(DATA_ROOM_RFI_FILE_TYPES).map(
  (value) => ({ value, label: `${value}_copy` }),
);

export const DOCS_FIELD_NAME = 'documents';
export const CUSTOM_DOCS_FIELD_NAME = 'customDocuments';
export const CUSTOM_NOTE_FIELD_NAME = 'customNote';
export const NOTIFICATION_MEDIUM_FIELD_NAME = 'notificationMedium';
export const CREATION_TYPE_FIELD_NAME = 'creationType';

export const isSpreadsheetMethod = (method: DataRoomRFICreationType) =>
  method === DATA_ROOM_RFI_CREATION_TYPE.SPREADSHEET;

export const getDocuments = () =>
  DOCUMENT_OPTIONS.map(({ value }) => ({
    name: value,
    period: undefined,
    checked: false,
  }));

export const getDocumentsForInitialValues = (
  checkedDocuments: RfiDocumentOption[] = [],
) => {
  const documents = getDocuments();

  if (getIsArrayEmpty(checkedDocuments))
    return { documents, customDocuments: [] as RfiDocumentOption[] };

  const checkedDoc: RfiDocumentOption[] = [];
  const checkedCustomDoc: RfiDocumentOption[] = [];

  checkedDocuments.forEach((el) => {
    if (Object.values(DATA_ROOM_RFI_FILE_TYPES).includes(el.name)) {
      checkedDoc.push(el);
    } else {
      checkedCustomDoc.push(el);
    }
  });

  const initialDocuments = documents.map(({ name }) => {
    const inChecked = checkedDoc.find((d) => d.name === name);
    return {
      name,
      period: inChecked?.period || undefined,
      checked: !!inChecked,
    };
  });

  return {
    documents: initialDocuments,
    customDocuments: checkedCustomDoc.map((el) => ({ ...el, checked: true })),
  };
};

export const hasSelectedDocument = (
  documents: Array<RfiDocumentOption | ParsedSpreadsheetRFIRow> = [],
  customDocuments: RfiDocumentOption[] = [],
  creationType?: DataRoomRFICreationType,
) =>
  isSpreadsheetMethod(creationType)
    ? (documents as ParsedSpreadsheetRFIRow[]).some(
        ({ status }) =>
          status === PARSED_SPREADSHEET_RFI_ROW_STATUS.READY_TO_ADD,
      )
    : [...documents, ...customDocuments].some(
        (document) => (document as RfiDocumentOption).checked,
      );

export const RFI_CREATE_STEPS = {
  DOCUMENTS: 0,
  DETAILS: 1,
} as const;

export const isNotFirstPage = (step: number) =>
  step !== RFI_CREATE_STEPS.DOCUMENTS;

export const STEP_SECTIONS = [
  {
    Step: StepRequestMethod,
    title: 'howDoYouWantToRequestDocuments',
    page: RFI_CREATE_STEPS.DOCUMENTS,
    hideForTemplate: true,
  },
  {
    Step: StepDocuments,
    title: 'specifyTheDocuments',
    page: RFI_CREATE_STEPS.DOCUMENTS,
  },
  {
    Step: StepSettings,
    title: 'settings',
    page: RFI_CREATE_STEPS.DETAILS,
  },
  {
    Step: StepShare,
    title: 'shareThisRequest',
    page: RFI_CREATE_STEPS.DETAILS,
  },
  {
    Step: StepGrantAccess,
    title: 'grantAccessToUploadedFiles',
    page: RFI_CREATE_STEPS.DETAILS,
    hideForTemplate: true,
  },
  {
    Step: StepTemplate,
    title: 'makeTemplate',
    page: RFI_CREATE_STEPS.DETAILS,
    hideForSpreadsheet: true,
  },
];

export const getNotificationMediumFormValue = (
  notificationMedium: NotificationMedium[] = [],
) => ({
  [NOTIFICATION_MEDIUM.EMAIL]: notificationMedium.includes(
    NOTIFICATION_MEDIUM.EMAIL,
  ),
  [NOTIFICATION_MEDIUM.SMS]: notificationMedium.includes(
    NOTIFICATION_MEDIUM.SMS,
  ),
  [NOTIFICATION_MEDIUM.PLATFORM]: notificationMedium.includes(
    NOTIFICATION_MEDIUM.PLATFORM,
  ),
});

export const getReadyDocumentsCount = (rows: ParsedSpreadsheetRFIRow[]) =>
  getArrayLength(
    rows.filter(
      ({ status }) => status === PARSED_SPREADSHEET_RFI_ROW_STATUS.READY_TO_ADD,
    ),
  );

export const sortParsedSpreadsheetRows = (
  rows: ParsedSpreadsheetRFIRow[],
  sort?: string,
) => {
  if (!sort) return rows;

  const sortBy = getSortBy(sort) as keyof ParsedSpreadsheetRFIRow;
  const isDesc = getIsDesc(sort);

  return [...rows].sort((a, b) =>
    getSortComparator(a[sortBy], b[sortBy], isDesc),
  );
};

export const filterParsedSpreadsheetRows = (
  rows: ParsedSpreadsheetRFIRow[],
  statusFilter: string,
  search: string,
) => {
  const searchValue = normalizeText(search);

  return rows.filter((row) => {
    const matchesStatus =
      statusFilter === RFI_LIST_STATUS_FILTER_ALL ||
      row.status === statusFilter;

    const matchesSearch =
      !searchValue || normalizeText(row.document).includes(searchValue);

    return matchesStatus && matchesSearch;
  });
};
